import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setShippingAddress,
  setBillingAddress,
  toggleUseSameAsShipping,
  removeAllAddresses,
  fetchAddresses,
} from "../../Providers/addressProvider";
import AddressPopup from "./AddressPopup";
import Footer from "../Footer/footer";
import Navbar from "../Navbar/navbar";
import { useNavigate } from "react-router-dom";
import SavedAddressesPopup from "./savedAddressesPopup";
import icons from "../../icons.config";
import FooterDoodles from "../Footer/footerDoodles";
import { IMAGE_URL } from "../../api-urls";
import { createOrderAPI, verifyPayment } from "../../Providers/ordersProvider";
import { getUserProfileAPI } from "../../Providers/profileProvider";
import ReasonPopup from "./modals/reasonPopUp";
import OrderSuccessPopup from "./modals/orderSuccessPopup";

const Checkout = ({ fromCart }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [successPopupData, setSuccessPopupData] = useState({ show: false, orderId: null });
  const razorpayInstanceRef = useRef(null);

  const { cartItems, subTotal, discount, couponCode, gst } = useSelector(
    (state) => state.cartItems
  );
  const { useSameAsShipping } = useSelector((state) => state?.address);
  const savedAddresses = useSelector((state) => state.address.savedAddresses);
  const shippingAddress = useSelector((state) => state.address?.shippingAddress);
  const billingAddress = useSelector((state) => state.address?.billingAddress);

  const [paymentMethod, setPaymentMethod] = useState("PREPAID");
  const [showShippingPopup, setShowShippingPopup] = useState(false);
  const [showBillingPopup, setShowBillingPopup] = useState(false);
  const [showSavedAddressesPopup, setShowSavedAddressesPopup] = useState(false);
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [defaultType, setDefaultType] = useState("shipping");

  // Razorpay script loading
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => setError("Failed to load Razorpay SDK");
    document.body.appendChild(script);
    return () => document.body.removeChild(script);
  }, []);

  useEffect(() => {
    dispatch(fetchAddresses());
  }, [dispatch]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await getUserProfileAPI();
        if (response?.data) setUser(response.data);
        else setError(response.error || "Unexpected response format");
      } catch (error) {
        setError("Failed to fetch user profile");
      }
    };
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (savedAddresses.length > 0 && !shippingAddress) {
      const defaultShipping = savedAddresses.find(
        (item) => item.isDefaultShippingAddress
      );
      if (defaultShipping) dispatch(setShippingAddress(defaultShipping));
    }
  }, [savedAddresses, shippingAddress, dispatch]);

  useEffect(() => {
    if (useSameAsShipping && shippingAddress) {
      dispatch(setBillingAddress(shippingAddress));
    }
  }, [useSameAsShipping, shippingAddress, dispatch]);

  const handleToggleUseSameAsShipping = () => {
    dispatch(toggleUseSameAsShipping());
    if (!useSameAsShipping && shippingAddress) {
      dispatch(setBillingAddress(shippingAddress));
    }
  };

  // Handle scrolling behavior for popups
  useEffect(() => {
    if (
      showShippingPopup ||
      showBillingPopup ||
      showSavedAddressesPopup ||
      successPopupData.show
    ) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [
    showShippingPopup,
    showBillingPopup,
    showSavedAddressesPopup,
    successPopupData.show,
  ]);

  const filteredCart = cartItems.filter((cartItem) => cartItem.isAvailable);
  const finalTotalPrice = subTotal - discount + gst;
  const fullAddress = shippingAddress
    ? `${shippingAddress.main_street}, ${shippingAddress.landmark}, ${shippingAddress.city}, ${shippingAddress.state} - ${shippingAddress.postal_code}, ${shippingAddress.country}`
    : "";

  const handleBackClick = () => setShowReasonPopup(true);

  const handlePlaceOrder = async () => {
    setLoading(true);
    try {
      if (!filteredCart.length) throw new Error("Your cart is empty.");
      if (!shippingAddress) throw new Error("Please provide a shipping address.");
      if (!billingAddress) throw new Error("Please provide a billing address.");

      const userData = user || (await getUserProfileAPI())?.data;
      if (!userData) throw new Error("Failed to fetch user details.");

      const orderData = {
        userId: userData._id,
        cartItems,
        shippingAddress,
        billingAddress,
        shipping_address: shippingAddress._id,
        billing_address: billingAddress._id,
        amount: finalTotalPrice,
        currency: "INR",
        payment_type: paymentMethod === "COD" ? "COD" : "Online",
        house_number: shippingAddress.main_street,
        landmark: shippingAddress.landmark,
        city: shippingAddress.city,
        state: shippingAddress.state,
        country: shippingAddress.country,
      };

      const createdOrder = await createOrderAPI(orderData);
      if (!createdOrder || !createdOrder.order || !createdOrder.order[0]) {
        throw new Error("Failed to create order or invalid response format.");
      }

      const orderId = createdOrder.order[0].id; // Use 'id' from API response

      if (paymentMethod === "COD") {
        setSuccessPopupData({ show: true, orderId });
        setLoading(false);
      } else {
        if (!razorpayLoaded || !window.Razorpay)
          throw new Error("Razorpay SDK is not loaded yet.");

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY || "rzp_test_CdCv3VaXvPMrSY",
          amount: createdOrder.order[0].total_amount * 100, // Use total_amount from response
          currency: "INR",
          name: "DIKOST",
          description: "Payment for your order",
          order_id: createdOrder.order[0]._id, // Use '_id' for Razorpay order_id if needed
          handler: async (response) => {
            try {
              const data = {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                payment_type: "Online",
                user_id: userData._id,
                finalShippingAddress: createdOrder.order[0].shipping_address,
                billingAddressDoc: createdOrder.order[0].billing_address,
              };
              await verifyPayment(data);
              setSuccessPopupData({ show: true, orderId });
              setLoading(false);
            } catch (error) {
              alert(`Payment Verification Failed: ${error.message}`);
              setLoading(false);
            }
          },
          prefill: {
            name: userData?.name || "Customer",
            email: userData?.email || "customer@example.com",
            contact: userData?.phone || "9999999999",
          },
          notes: { order_id: createdOrder.order[0]._id },
          theme: { color: "#3399cc" },
        };

        const razorpayInstance = new window.Razorpay(options);
        razorpayInstance.on("payment.failed", (response) => {
          alert(`Payment Failed: ${response.error.description}`);
          setLoading(false);
        });
        razorpayInstance.open();
      }
    } catch (error) {
      alert(`Failed to place the order: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <section className="relative min-h-screen">
      <Navbar />
      <div className="w-full h-full py-16 md:py-24 xl:py-32 relative top-12 px-12 bg-tertiary text-primary1 lg:px-24 xl:px-32 overflow-y-auto">
        <div className="absolute -top-8 -right-1 lg:-top-14 lg:-right-20 z-10">
          <img
            src={icons.trendCircles}
            alt="Doodle Left"
            className="w-44 md:w-60 lg:w-4/5"
          />
        </div>
        <div className="flex justify-between items-center pb-4">
          <button onClick={handleBackClick} className="text-sm text-blue-600">
            <img src={icons.back} alt="back button" />
          </button>
          <h1 className="text-4xl font-normal font-outfit text-center text-primary1">
            CHECKOUT
          </h1>
          <div />
        </div>
        {showReasonPopup && (
          <ReasonPopup onClose={() => setShowReasonPopup(false)} />
        )}
        <hr className="border-secondary border-[1px] mb-4" />

        <div className="mt-6 space-y-6 lg:grid lg:grid-cols-10 lg:gap-6 lg:space-y-0">
          <div className="col-span-6 space-y-6 overflow-y-auto max-h-full lg:max-h-none">
            <div>
              <h2 className="text-2xl font-outfit font-light text-primary1">
                Shipping Details
              </h2>
              {!shippingAddress || Object.keys(shippingAddress).length === 0 ? (
                <div>
                  <p className="text-primary1 font-poppins font-light text-xl">
                    Default Name
                  </p>
                  <div className="flex items-center space-x-2">
                    <img src={icons.eMail} alt="Email Icon" className="w-5 h-5" />
                    <p className="text-hcolor font-poppins font-light text-base">
                      default@example.com
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <img src={icons.phone} alt="Phone Icon" className="w-5 h-5" />
                    <p className="text-hcolor font-poppins font-light text-base">
                      123-456-7890
                    </p>
                  </div>
                  <button
                    className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded"
                    onClick={() => setShowShippingPopup(true)}
                  >
                    Add Address
                  </button>
                </div>
              ) : (
                <div className="mt-4 lg:flex lg:justify-between lg:items-end">
                  <div>
                    <p className="text-primary1 font-poppins font-light text-xl">
                      {shippingAddress.first_name} {shippingAddress.last_name}
                    </p>
                    <div className="flex items-center space-x-2">
                      <img src={icons.eMail} alt="Email Icon" className="w-5 h-5" />
                      <p className="text-hcolor font-poppins font-light text-base">
                        {shippingAddress.email_id}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <img src={icons.phone} alt="Phone Icon" className="w-5 h-5" />
                      <p className="text-hcolor font-poppins font-light text-base">
                        {shippingAddress.mobile_number}
                      </p>
                    </div>
                    <div className="flex items-start space-x-2">
                      <img
                        src={icons.address}
                        alt="Location Icon"
                        className="w-5 h-5"
                      />
                      <p className="text-hcolor font-poppins font-light text-base">
                        {fullAddress}
                      </p>
                    </div>
                  </div>
                  <p
                    className="mt-4 lg:mt-0 px-4 py-2 text-primary1 underline text-base cursor-pointer"
                    onClick={() => {
                      setDefaultType("shipping");
                      setShowSavedAddressesPopup(true);
                    }}
                  >
                    Change Address
                  </p>
                </div>
              )}
            </div>

            <div>
              <h2 className="text-2xl font-outfit font-light text-primary1">
                Billing Address
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 mt-4">
                <label className="flex items-center border-2 border-secondary rounded-md mb-2 sm:mb-0 py-2 px-3 font-poppins font-normal">
                  <input
                    type="radio"
                    className="mr-2 accent-hcolor"
                    checked={useSameAsShipping}
                    onChange={handleToggleUseSameAsShipping}
                  />
                  Same as shipping address
                </label>
                <label className="flex items-center border-2 border-secondary rounded-md py-2 px-3 font-poppins font-normal">
                  <input
                    type="radio"
                    className="mr-2 accent-hcolor"
                    checked={!useSameAsShipping}
                    onChange={handleToggleUseSameAsShipping}
                  />
                  Use a different billing address
                </label>
              </div>
              {useSameAsShipping ? (
                <p className="mt-4">Billing Address is same as Shipping Address</p>
              ) : billingAddress ? (
                <div className="mt-4">
                  <p className="text-primary1 font-poppins font-light text-xl">
                    {billingAddress.first_name} {billingAddress.last_name}
                  </p>
                  <div className="flex items-center space-x-2">
                    <img src={icons.eMail} alt="Email Icon" className="w-5 h-5" />
                    <p className="text-hcolor font-poppins font-light text-base">
                      {billingAddress.email_id}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <img src={icons.phone} alt="Phone Icon" className="w-5 h-5" />
                    <p className="text-hcolor font-poppins font-light text-base">
                      {billingAddress.mobile_number}
                    </p>
                  </div>
                  <div className="flex items-start space-x-2">
                    <img
                      src={icons.address}
                      alt="Location Icon"
                      className="w-5 h-5"
                    />
                    <p className="text-hcolor font-poppins font-light text-base">
                      {billingAddress.main_street}, {billingAddress.landmark},{" "}
                      {billingAddress.city}, {billingAddress.state} -{" "}
                      {billingAddress.postal_code}, {billingAddress.country}
                    </p>
                  </div>
                  <p
                    className="mt-4 text-primary1 underline text-base cursor-pointer text-right"
                    onClick={() => {
                      setDefaultType("billing");
                      setShowSavedAddressesPopup(true);
                    }}
                  >
                    Change Billing Address
                  </p>
                </div>
              ) : (
                <button
                  className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded"
                  onClick={() => {
                    setDefaultType("billing");
                    setShowSavedAddressesPopup(true);
                  }}
                >
                  Add Billing Address
                </button>
              )}
            </div>

            <div>
              <h2 className="text-2xl font-light font-outfit">Payment Option</h2>
              <div className="flex flex-col sm:flex-row sm:space-x-4 mt-4">
                <label className="flex items-center mb-2 sm:mb-0 border-2 border-secondary rounded-md py-2 px-3 font-poppins font-normal">
                  <input
                    type="radio"
                    className="mr-2 accent-hcolor"
                    name="paymentMethod"
                    checked={paymentMethod === "PREPAID"}
                    onChange={() => setPaymentMethod("PREPAID")}
                  />
                  Pay Online
                </label>
                <label className="flex items-center border-2 border-secondary rounded-md py-2 px-3 font-poppins font-normal">
                  <input
                    type="radio"
                    className="mr-2 accent-hcolor"
                    name="paymentMethod"
                    checked={paymentMethod === "COD"}
                    onChange={() => setPaymentMethod("COD")}
                  />
                  COD
                </label>
              </div>
            </div>
          </div>

          <div className="space-y-4 lg:col-span-4">
            <h2 className="text-xl font-outfit font-light">Order Details</h2>
            <div className="max-h-36 lg:max-h-28 overflow-y-auto">
              {filteredCart.filter((item) => item.isSelected).length > 0 ? (
                filteredCart
                  .filter((item) => item.isSelected)
                  .map((item, index) => (
                    <div
                      key={index}
                      className="py-4 flex items-center space-x-4"
                    >
                      <img
                        src={IMAGE_URL + item.variant_main_image}
                        alt={item.product_name}
                        className="w-28 h-44 md:w-16 md:h-24"
                      />
                      <div className="flex-1">
                        <p className="font-outfit font-light text-lg lg:text-xl">
                          {item.product_name.toUpperCase()}
                        </p>
                        <p className="text-hcolor">
                          Size: {item.size || "Your Custom Size"}
                        </p>
                        <p className="text-hcolor">
                          Color: {item.color || "Default"}
                        </p>
                        <p className="text-hcolor">Quantity: {item.quantity}</p>
                      </div>
                      <p className="ml-auto font-normal text-hcolor text-right">
                        Rs.{(item.discount_price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))
              ) : (
                <p className="text-red-500 text-center">
                  No selected items available for checkout.
                </p>
              )}
            </div>
            <hr className="border-secondary my-2" />
            <div className="space-y-2">
              <p className="text-base font-poppins text-hcolor flex font-light">
                Subtotal:{" "}
                <span className="ml-auto text-right">
                  Rs.{subTotal.toFixed(2)}
                </span>
              </p>
              <p className="text-base font-poppins text-hcolor flex font-light">
                GST (18%):{" "}
                <span className="ml-auto text-right">Rs.{gst.toFixed(2)}</span>
              </p>
              {discount > 0 && (
                <p className="text-base font-poppins text-hcolor flex font-light">
                  Coupon Discount:{" "}
                  <span className="ml-auto">- Rs.{discount.toFixed(2)}</span>
                </p>
              )}
            </div>
            <hr className="border-secondary my-2" />
            <p className="text-xl font-normal text-primary1 flex justify-between">
              <span>Total</span>
              <span>Rs.{finalTotalPrice.toFixed(2)} INR</span>
            </p>
            {error && <p className="text-red-500">Error: {error}</p>}
            <button
              className="mt-4 px-6 py-2 bg-hcolor hover:bg-tertiary text-tertiary hover:text-hcolor hover:border font-normal text-lg w-full"
              onClick={handlePlaceOrder}
              disabled={loading || !razorpayLoaded}
            >
              {loading
                ? "Processing..."
                : !razorpayLoaded
                ? "Loading Razorpay..."
                : "PLACE ORDER"}
            </button>
          </div>
        </div>
      </div>

      {showShippingPopup && (
        <AddressPopup
          onClose={() => setShowShippingPopup(false)}
          shippingAddress={shippingAddress}
          billingAddress={billingAddress}
          isBilling={false}
        />
      )}
      {showBillingPopup && (
        <AddressPopup
          onClose={() => setShowBillingPopup(false)}
          shippingAddress={shippingAddress}
          billingAddress={billingAddress}
          isBilling={true}
        />
      )}
      {showSavedAddressesPopup && (
        <SavedAddressesPopup
          onClose={() => setShowSavedAddressesPopup(false)}
          onSelectAddress={(address) =>
            defaultType === "shipping"
              ? dispatch(setShippingAddress(address))
              : dispatch(setBillingAddress(address))
          }
          defaultType={defaultType}
        />
      )}
      {successPopupData.show && <OrderSuccessPopup orderId={successPopupData.orderId} />}
      <div className="relative">
        <FooterDoodles />
        <Footer />
      </div>
    </section>
  );
};

export default Checkout;